import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';
import FileSaver from 'filesaver.js';

import * as api from '../lib/api';
import {withUser} from '../lib/user';
import withUrlState from '../lib/with-url-state';
import Screen from '../components/Screen';
import Tabs from '../components/Tabs';
import Content from '../components/Content';
import Card from '../components/Card';
import Button from '../components/Button';
import Loading from '../components/Loading';
import {default as ErrorMessage} from '../components/Error';
import Icon from '../components/Icon';
import Menu from '../components/Menu';
import ReportParametersModal from '../components/ReportParametersModal';
import Role from "../lib/Role";

class ReportViewerScreen extends PureComponent {
  viewer = React.createRef();
  state = {
    loading: true,
    error: false,
    report: null,
    catalog: [],
    optionsOpen: false,
    downloading: false,
    printing: false,
    downloadMenuOpen: false,
    editParameters: false
  };

  componentDidMount() {
    const {urlState, onUrlStateChange} = this.props;
    this.runReport(urlState);

    // If the URL changes, re-run the report.
    onUrlStateChange(urlState => this.runReport(urlState));
  }

  async runReport(parameters) {
    const {credentials, match: {params: {id}}} = this.props;

    this.setState({loading: true, error: false});

    try {
      const [reports, catalog] = await Promise.all([
        api.getReports(credentials),
        api.getCatalog(credentials)
      ]);
      const report = reports.filter(r => r.id === id)[0];

      if (!report) {
        throw new Error('Report not found with ID=' + id);
      }

      // Start running the report.
      const content = await api.runHtmlReport(report.id, parameters, credentials);

      this.setState({report, catalog, loading: false}, () => {
        const handle = this.viewer.current;
        handle.innerHTML = content;
      });
    }
    catch (e) {
      if (e.status && e.status === 204) {
        this.setState({error: 'no-data', loading: false});
      }
      else {
        this.setState({error: 'general', loading: false});
      }
    }
  }

  toggleReportOptions = () => {
    this.setState(prevState => ({
      optionsOpen: !prevState.optionsOpen
    }));
  }

  toggleParameters = () => {
    this.setState(prevState => ({
      editParameters: !prevState.editParameters
    }));
  }

  toggleDownloads = () => {
    this.setState(prevState => ({
      downloadMenuOpen: !prevState.downloadMenuOpen
    }))
  }

  downloadReport = async (format) => {
    const {credentials, urlState} = this.props;
    const {report} = this.state;
    const now = new Date();

    this.setState({downloading: true});
    try {
      let content;
      switch(format) {
        case 'pdf':
          content = await api.runPdfReport(report.id, urlState, credentials);
          break;
        case 'xlsx':
          content = await api.runXlsxReport(report.id, urlState, credentials);
          break;
        default:
          throw new Error("invalid Format");
      }

      FileSaver.saveAs(content, `${report.name}-${now.getTime()}.${format}`);
    }
    catch(e) {
      NotificationManager.error(`Unable to export to ${format}. Please try again.`);
    }
    finally {
      this.setState({downloading: false});
    }
  }

  printReport = async () => {
    const {credentials, urlState} = this.props;
    const {report} = this.state;

    // A browser that will not print a PDF from an iframe prints it from a tab instead, and the tab has to be opened
    // now, while the click is still being handled. Opened once the PDF has come back, it is no longer anything the
    // user asked for, and the browser blocks it as a pop-up.
    const tab = printsPdfInIframe() ? null : openPrintTab();

    if (tab === false) {
      NotificationManager.error('Your browser blocked the print window. Please allow pop-ups for this site.');
      return;
    }

    this.setState({printing: true});

    try {
      // The same PDF the download gives, printed through the browser's own viewer.
      const content = await api.runPdfReport(report.id, urlState, credentials);

      if (tab) {
        printPdfInTab(tab, content);
      }
      else {
        printPdfInIframe(content);
      }
    }
    catch (e) {
      if (tab) {
        tab.close();
      }

      NotificationManager.error('Unable to print this report. Please try again.');
    }
    finally {
      this.setState({printing: false});
    }
  }

  handlePrintReport = async () => {
    await this.printReport();
  }

  handleDownloadPdf = async () => {
    await this.downloadReport('pdf');
  }

  handleDownloadXlsx = async () => {
    await this.downloadReport('xlsx');
  }

  handleRunReport = (report, parameters) => {
    this.toggleParameters();
    this.props.setUrlState(parameters);
  }

  renderReport() {
    const {user, urlState} = this.props;
    const {report, catalog, loading, error, optionsOpen, downloading, printing, editParameters, downloadMenuOpen} = this.state;

    if (loading) {
      return <Loading message="Running report..." />;
    }

    if (error) {
      return <ErrorMessage message="Report failed to run!" />;
    }

    return (
      <>
        <Content>
          <Card>
            <Card.Toolbar
              start={<ReportTitle>{report.name}</ReportTitle>}
              end={
                <>
                  <Button.Group>
                    <Button id="print-report" onClick={this.handlePrintReport} disabled={printing}>
                      {printing ? 'Preparing...' : 'Print Report'}
                    </Button>
                    <Button id="download-options" onClick={this.toggleDownloads} disabled={downloading}>
                      {downloading ? 'Downloading...' : 'Download Report'}
                    </Button>
                    {(report.parameters.length > 0 || report.published || (!report.published && Role.ADMIN) ||
                        (report.published && Role.MANAGER)) && (
                      <Button id="report-options" onClick={this.toggleReportOptions}>
                        <Icon name="chevron-down" />
                      </Button>
                    )}
                  </Button.Group>
                  <Menu 
                    open={downloadMenuOpen}
                    position="bottom-end"
                    relativeTo="download-options"
                    onClose={this.toggleDownloads}>
                      <Menu.Option onClick={this.handleDownloadPdf}>As PDF</Menu.Option>
                      <Menu.Option onClick={this.handleDownloadXlsx}>As XLSX</Menu.Option>
                  </Menu>
                  <Menu
                    open={optionsOpen}
                    position="bottom-end"
                    relativeTo="report-options" 
                    onClose={this.toggleReportOptions}>
                    {report.parameters.length > 0 && (
                      <Menu.Option onClick={this.toggleParameters}>Edit Parameters</Menu.Option>
                    )}
                    {report.published && (
                      <Menu.Option to={`/reports/schedules?report=${report.id}`}>View Schedules</Menu.Option>
                    )}
                    {!report.published && user.role === Role.ADMIN && (
                      <Menu.Option onClick={() => console.log('publish it')}>Publish Report</Menu.Option>
                    )}
                  </Menu>
                </>
              }
            />
            <Card.Content>
              <ReportViewer ref={this.viewer} />
            </Card.Content>
          </Card>
        </Content>
        <ReportParametersModal
          catalog={catalog}
          report={editParameters && report}
          parameters={urlState}
          onRun={this.handleRunReport}
          onClose={this.toggleParameters}
        />
      </>
    );
  }

  render() {
    return (
      <Screen
        title="Reports"
        activeLink="Reports"
        tabs={
          <Tabs>
            <Tabs.Tab active to="/reports">Reports</Tabs.Tab>
            <Tabs.Tab to="/reports/schedules">Schedules</Tabs.Tab>
          </Tabs>
        }>
        {this.renderReport()}
      </Screen>
    );
  }
}

const ReportTitle = styled(Card.Title)`
  h4 {
    padding: 0;
  }
`;

const ReportViewer = styled.div`
  overflow-x: auto;
`;

// How long to wait for the iframe's viewer to load before giving up on it, and how long the PDF is kept afterwards so
// the print dialog has something to read from.
const PRINT_LOAD_TIMEOUT = 10000;
const PRINT_CLEANUP_DELAY = 60000;
// How often to look for the print tab having been closed, so its PDF can be let go.
const PRINT_TAB_CLOSED_INTERVAL = 5000;

/**
 * Whether this browser prints a PDF held in a hidden iframe. Chromium browsers - Chrome, Edge, Opera - do. Firefox and
 * Safari do not reliably, and Chrome on iOS is Safari underneath, so they are given a tab to print from instead.
 */
function printsPdfInIframe() {
  return /(Chrome|Chromium|Edg)\//.test(navigator.userAgent);
}

/**
 * Opens the tab a report will be printed from, showing that it is on its way. Returns false if the browser blocked it.
 */
function openPrintTab() {
  const tab = window.open('', '_blank');

  if (!tab) {
    return false;
  }

  try {
    tab.document.title = 'Preparing report...';
    tab.document.body.textContent = 'Preparing report...';
  }
  catch (e) {
    // Only a courtesy while the report runs. The tab is pointed at the PDF either way.
  }

  return tab;
}

/**
 * Shows a PDF in a tab opened by openPrintTab, to be printed from the browser's own viewer: the colours, fonts and page
 * breaks the report was set up with, exactly as they are when the report is downloaded and printed by hand.
 */
function printPdfInTab(tab, blob) {
  if (tab.closed) {
    // Closed while the report was running, so there is nowhere to show it.
    return;
  }

  const blobUrl = URL.createObjectURL(blob);
  tab.location.href = blobUrl;

  // The tab can go back to the url for as long as it is open - reloaded, saved, printed again - so the PDF is only let
  // go once the tab is closed. Otherwise every print would hold its PDF in memory until this page is reloaded.
  const closedCheck = setInterval(() => {
    if (tab.closed) {
      clearInterval(closedCheck);
      URL.revokeObjectURL(blobUrl);
    }
  }, PRINT_TAB_CLOSED_INTERVAL);
}

/**
 * Prints a PDF through the browser's own viewer from a hidden iframe, which goes straight to the print dialog.
 */
function printPdfInIframe(blob) {
  const blobUrl = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  let handled = false;

  const cleanUp = () => {
    URL.revokeObjectURL(blobUrl);

    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  };

  // A tab cannot be opened from here - the click is long over, and the browser would block it - so a viewer that
  // will not print is pointed at the download instead.
  const fail = () => {
    if (handled) {
      return;
    }

    handled = true;
    cleanUp();
    NotificationManager.error('Unable to print this report here. Please use Download Report and print the file.');
  };

  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.src = blobUrl;

  iframe.onload = () => {
    if (handled) {
      return;
    }

    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      handled = true;

      // Kept around while the dialog is open: revoking the url or removing the iframe under it leaves the viewer
      // with nothing to print.
      setTimeout(cleanUp, PRINT_CLEANUP_DELAY);
    }
    catch (e) {
      fail();
    }
  };

  // A viewer that never loads - PDFs set to download rather than open, say - never fires onload, so the wait is
  // capped rather than leaving the user looking at nothing.
  setTimeout(fail, PRINT_LOAD_TIMEOUT);

  document.body.appendChild(iframe);
}

export default withUrlState({})(withUser(ReportViewerScreen));